import { useMemo, ChangeEvent, useCallback, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import { Card, Table, TableBody, useMediaQuery, TableContainer } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { ListEntityType } from 'src/services/lists/lists.types';
import { PageOptions, PaginatedResponse, SortConfig } from 'src/services/api.types';
import { ICompanyProfile } from 'src/services/connections/connections.types';

import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import { SortOrder } from 'src/services/common.types';
import { useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import { debounce } from 'lodash';
import BulkCompanyImportModal from 'src/sections/companies/modal/bulk-company-import-modal';
import CompaniesTableToolbar, { ToolbarOptions } from './companies-table-toolbar';
import CompaniesTableRow from './companies-table-row';

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

type Props = {
  toolbarOptions?: ToolbarOptions;
};

export default function CompaniesTable({
  toolbarOptions
}: Props) {

  const table = useTable();

  const tenantType = useOrgTenant();

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const denseHeight = table.dense ? 56 : 56 + 20;

  const addToListControl = useBoolean();
  const bulkImportControl = useBoolean();

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'created_at', sort_order: SortOrder.DESC });
  const [filterQuery, setFilterQuery] = useState<string>('');

  const { currentData: companies, isLoading: isConnectionsLoading } = useGetMyCompaniesQuery(
    {
      params: {
        ...pageOptions,
        ...sortConfig,
        search_query: filterQuery
      },
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const tableLabels = useMemo(
    () => [
      { id: 'title', label: t('connections.table.company_name') },
      { id: 'phone', label: t('connections.table.phone') },
      { id: 'owner', label: t('connections.table.owner') },
      { id: 'location', label: t('connections.table.location') },
      { id: 'status', label: t('connections.table.last_activity') },
      { id: '' },
    ],
    [t]
  );

  const notFound = !companies?.count;

  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleFilters = useMemo(() => debounce((value: string) => {
    setFilterQuery(value);
  }, 500), [setFilterQuery]);

  const handleAddToList = useCallback(async (companyIds: string[]) => {
    addToListControl.onTrue();
  }, [addToListControl]);

  const onSuccessAddToList = useCallback(() => {
    table.onSelectAllRows(false);
    addToListControl.onFalse();
  }, [addToListControl, table]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return (
    <Card sx={{ pt: { xs: 2, sm: 'unset' } }}>

      {
        isConnectionsLoading ? (
          <TableSkeleton />
        )
          :
          <>
            <CompaniesTableToolbar
              table={table}
              options={toolbarOptions}
              onFilters={handleFilters}
              onAddToList={handleAddToList}
              onSortChange={handleSortChange}
              onBulkImport={bulkImportControl.onTrue}
            />

            <TableContainer sx={{ overflow: 'unset' }}>
              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'}>

                  {
                    !isMobile && (
                      <TableHeadCustom
                        headLabel={tableLabels}
                        rowCount={companies?.results.length ?? 0}
                        numSelected={table.selected.length}
                        onSelectAllRows={(checked) =>
                          table.onSelectAllRows(
                            checked,
                            (companies?.results ?? []).map((row) => row.id) as string[]
                          )
                        }
                      />
                    )
                  }

                  <TableBody >
                    {companies?.results?.map((row) =>
                      <CompaniesTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                      />
                    )}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, companies?.count ?? 0)}
                    />

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={companies?.count ?? 0}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </>
      }

      <AddItemsModal
        open={addToListControl.value}
        onClose={addToListControl.onFalse}
        onSuccess={onSuccessAddToList}
        items={table.selected}
        entityType={ListEntityType.COMPANY}
      />

      <BulkCompanyImportModal open={bulkImportControl.value} onClose={bulkImportControl.onFalse} />

    </Card>
  );
}
